import { useState, useMemo, useEffect } from 'react'

export interface TableColumn {
  id: string
  label: string
  visible: boolean
  sortable?: boolean
}

type SortDirection = 'asc' | 'desc'

// eslint-disable-next-line max-params
export function useTableControls<T>(
  data: T[],
  defaultColumns: TableColumn[],
  defaultRowsPerPage = 10,
  defaultSortBy: string | null = null,
  defaultSortDirection: SortDirection = 'asc'
) {
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage)
  const [columns, setColumns] = useState<TableColumn[]>(defaultColumns)
  const [sortBy, setSortBy] = useState<string | null>(defaultSortBy)
  const [sortDirection, setSortDirection] =
    useState<SortDirection>(defaultSortDirection)

  // Sync column labels when defaultColumns change (e.g. language switch)
  useEffect(() => {
    setColumns((prev) =>
      prev.map((col) => {
        const updated = defaultColumns.find((d) => d.id === col.id)
        return updated ? { ...col, label: updated.label } : col
      })
    )
  }, [defaultColumns])

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const toggleColumn = (columnId: string) => {
    setColumns((prev) =>
      prev.map((col) =>
        col.id === columnId ? { ...col, visible: !col.visible } : col
      )
    )
  }

  const handleSort = (columnId: string) => {
    if (sortBy === columnId) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(columnId)
      setSortDirection('asc')
    }
    setPage(0)
  }

  const visibleColumns = useMemo(
    () => columns.filter((col) => col.visible),
    [columns]
  )

  const sortedData = useMemo(() => {
    if (!sortBy) return data

    return [...data].sort((a, b) => {
      const aValue = (a as Record<string, unknown>)[sortBy]
      const bValue = (b as Record<string, unknown>)[sortBy]

      if (aValue === null && bValue === null) return 0
      if (aValue === undefined && bValue === undefined) return 0
      if (aValue === null || aValue === undefined) return 1
      if (bValue === null || bValue === undefined) return -1

      let comparison = 0
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        comparison = aValue.toLowerCase().localeCompare(bValue.toLowerCase())
      } else if (typeof aValue === 'number' && typeof bValue === 'number') {
        comparison = aValue - bValue
      } else {
        comparison = String(aValue).localeCompare(String(bValue))
      }

      return sortDirection === 'asc' ? comparison : -comparison
    })
  }, [data, sortBy, sortDirection])

  const paginatedData = useMemo(() => {
    const start = page * rowsPerPage
    return sortedData.slice(start, start + rowsPerPage)
  }, [sortedData, page, rowsPerPage])

  const resetPage = () => setPage(0)

  return {
    page,
    rowsPerPage,
    columns,
    visibleColumns,
    paginatedData,
    sortBy,
    sortDirection,
    handleChangePage,
    handleChangeRowsPerPage,
    toggleColumn,
    handleSort,
    resetPage,
  }
}
